import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useDropzone } from "react-dropzone";
import { createIncident } from "../../../screens/IncidentManagement/services/service";
import {
  createSecurityIncident,
  updateSecurityIncident,
} from "../../../service/securityService";
import { useAuth } from "../../../auth/AuthProvider";
import {
  SecurityIncidentFormData,
  SecurityIncidentType,
  IncidentSeverity,
} from "../../../types/security_types";
import { usePremisesStore, useOrgUsersStore, useAccessZonesStore } from "../../../store/store";
import FileUpload from "../../../components/FileUpload/FileUpload";
import { useS3Upload } from "../../../utils/s3Client";
import { Attachment } from "../../../types/api";

interface SecurityIncidentFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  isView?: boolean;
  initialData?: SecurityIncidentFormData;
  prefillAttachments?: Attachment[];
}

const SecurityIncidentForm: React.FC<SecurityIncidentFormProps> = ({
  onSuccess,
  onCancel,
  isView = false,
  initialData,
  prefillAttachments,
}) => {
  const { user, orgUser, orgAdmin } = useAuth();
  const { allPremises, fetchPremises } = usePremisesStore();
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();
  const { zonesData, fetchAllZones } = useAccessZonesStore();
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [files, setFiles] = useState<File[] | null>(null);
  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>([]);
  const [linkedLogsText, setLinkedLogsText] = useState("");

  const { uploadFileToS3, progress: s3UploadProgress, uploading: s3Uploading } = useS3Upload();

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      setFiles(acceptedFiles);
    },
    disabled: submitting || isView,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".gif", ".webp"],
      "video/*": [".mp4", ".avi", ".mov", ".webm"],
      "application/pdf": [".pdf"],
    },
  });

  const [formData, setFormData] = useState<SecurityIncidentFormData>(
    () =>
      initialData || {
        incidentType: "Unauthorized access",
        location: "",
        zoneId: "",
        dateTime: new Date().toISOString().slice(0, 16),
        severity: "Medium",
        description: "",
        immediateActionTaken: "",
        escalatedTo: [],
      }
  );

  useEffect(() => {
    fetchPremises();
    fetchAllZones();
    fetchOrgUsers();
  }, [fetchPremises, fetchOrgUsers, fetchAllZones]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        escalatedTo: initialData.escalatedTo || [],
      });
      setLinkedLogsText((initialData.linkedLogRefs || []).join("\n"));
    }
  }, [initialData]);

  useEffect(() => {
    if (prefillAttachments?.length) {
      setExistingAttachments(prefillAttachments);
    }
  }, [prefillAttachments]);

  const reporterDisplayName =
    [orgUser?.firstName, orgUser?.lastName].filter(Boolean).join(" ").trim() ||
    orgAdmin?.org_admin_name?.trim() ||
    user?.username ||
    user?.email ||
    "Security user";

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.incidentType) {
      newErrors.incidentType = "Incident type is required";
    }

    if (!formData.location.trim()) {
      newErrors.location = "Location is required";
    }

    if (!formData.dateTime) {
      newErrors.dateTime = "Date and time is required";
    }

    if (!formData.severity) {
      newErrors.severity = "Severity is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!formData.escalatedTo || formData.escalatedTo.length === 0) {
      newErrors.escalatedTo = "Escalated To is required — select at least one recipient";
    }

    const hasNewFiles = files && files.length > 0;
    const hasExisting = existingAttachments.length > 0;
    if (
      (formData.severity === "High" || formData.severity === "Critical") &&
      !hasNewFiles &&
      !hasExisting
    ) {
      newErrors.evidence = "Evidence is required for High/Critical severity incidents";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isView) {
      return;
    }

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      setSubmitting(true);

      const attachmentUrls: string[] = [];
      if (files && files.length > 0) {
        for (const file of files) {
          const s3Url = await uploadFileToS3(file);
          if (s3Url) attachmentUrls.push(s3Url);
        }
      }
      attachmentUrls.push(
        ...existingAttachments.map((a) => a.url || "").filter(Boolean)
      );

      const linkedLogRefs = linkedLogsText
        .split(/\n/)
        .map((l) => l.trim())
        .filter(Boolean);

      const selectedZone = formData.zoneId
        ? zonesData.find((z: any) => (z._id || z.id) === formData.zoneId)
        : null;
      const selectedPremise = selectedZone
        ? allPremises.find((p: any) => (p._id || p.id) === selectedZone.premiseId)
        : null;

      const securityRecord = await createSecurityIncident({
        ...formData,
        evidenceUrls: attachmentUrls,
        linkedLogRefs,
      });

      const secId = securityRecord.id || securityRecord._id;
      if (!secId) {
        throw new Error("Security incident id missing");
      }

      try {
        const globalRes = await createIncident({
          region: selectedPremise?.region || "",
          hospitalName: selectedPremise?.premisesName || formData.location,
          reportDate: new Date().toISOString(),
          incidentDate: new Date(formData.dateTime).toISOString(),
          reportedBy: reporterDisplayName,
          placeOfOccurrence: formData.location,
          whoWasInvolved: "Staff",
          department: "Security",
          criticalIncident:
            formData.severity === "High" || formData.severity === "Critical",
          whatWentWrong: formData.description,
          immediateActionTaken: formData.immediateActionTaken || "",
          whatShouldHaveHappened: "",
          category: formData.incidentType,
          severity: formData.severity,
          status: "Open",
          attachments: attachmentUrls.map((url) => ({ url })),
          sourceModule: "Security",
          moduleSpecificData: {
            securityIncidentId: String(secId),
            securityIncidentNumber: securityRecord.incidentNumber || "",
            escalatedToUserIds: formData.escalatedTo || [],
            linkedLogRefs,
          },
        });

        const globalIncident = globalRes.data as { id?: string; _id?: string };
        const globalId = globalIncident?.id || globalIncident?._id;
        if (globalRes.success && globalId) {
          await updateSecurityIncident(String(secId), {
            globalIncidentId: String(globalId),
          });
        }
      } catch (syncErr) {
        console.error(syncErr);
        toast.warning(
          "Security incident saved. Sync to the organization incident register failed — you can link it manually from Incident Management."
        );
      }

      toast.success("Security incident reported and registered");
      window.dispatchEvent(new CustomEvent("incidentCreated"));
      onSuccess();
    } catch (error: any) {
      console.error("Error reporting incident:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.resMessage ||
          "Failed to report incident"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const escalatedSummary = () => {
    if (!formData.escalatedTo?.length) return "—";
    return formData.escalatedTo
      .map((id) => {
        const u = orgUsersData.find((x: any) => (x._id || x.id) === id);
        if (!u) return id;
        return (
          [u.firstName, u.lastName].filter(Boolean).join(" ").trim() ||
          u.name ||
          id
        );
      })
      .join(", ");
  };

  return (
    <>
      <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
        {isView ? "Security incident details" : "Report security incident"}
      </h2>
      <form onSubmit={handleSubmit} id="incidentForm" className="px-6 pb-6">
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Incident Type <span className="text-red-500">*</span>
              </label>
              <select
                disabled={isView}
                value={formData.incidentType}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    incidentType: e.target.value as SecurityIncidentType,
                  });
                  setErrors({ ...errors, incidentType: "" });
                }}
                className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] ${
                  errors.incidentType ? "border-red-500" : "border-gray-300"
                }`}
                required
              >
                <option value="Unauthorized access">Unauthorized access</option>
                <option value="Theft">Theft</option>
                <option value="Violence / assault">Violence / assault</option>
                <option value="Missing patient">Missing patient</option>
                <option value="Cyber-physical breach">Cyber-physical breach</option>
                <option value="Suspicious object">Suspicious object</option>
              </select>
              {errors.incidentType && (
                <span className="text-red-500 text-xs">{errors.incidentType}</span>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Severity <span className="text-red-500">*</span>
              </label>
              <select
                disabled={isView}
                value={formData.severity}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    severity: e.target.value as IncidentSeverity,
                  });
                  setErrors({ ...errors, severity: "" });
                }}
                className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] ${
                  errors.severity ? "border-red-500" : "border-gray-300"
                }`}
                required
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
              {errors.severity && (
                <span className="text-red-500 text-xs">{errors.severity}</span>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Location <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              disabled={isView}
              value={formData.location}
              onChange={(e) => {
                setFormData({ ...formData, location: e.target.value });
                setErrors({ ...errors, location: "" });
              }}
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] ${
                errors.location ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter incident location"
              required
            />
            {errors.location && (
              <span className="text-red-500 text-xs">{errors.location}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Access Zone (Optional)
            </label>
            <select
              disabled={isView}
              value={formData.zoneId || ""}
              onChange={(e) => {
                setFormData({ ...formData, zoneId: e.target.value || undefined });
              }}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998]"
            >
              <option value="">Select zone (optional)</option>
              {zonesData.map((zone: any) => (
                <option key={zone._id || zone.id} value={zone._id || zone.id}>
                  {zone.zoneName}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Date & Time <span className="text-red-500">*</span>
            </label>
            <input
              type="datetime-local"
              disabled={isView}
              value={formData.dateTime}
              onChange={(e) => {
                setFormData({ ...formData, dateTime: e.target.value });
                setErrors({ ...errors, dateTime: "" });
              }}
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] ${
                errors.dateTime ? "border-red-500" : "border-gray-300"
              }`}
              required
            />
            {errors.dateTime && (
              <span className="text-red-500 text-xs">{errors.dateTime}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              disabled={isView}
              value={formData.description}
              onChange={(e) => {
                setFormData({ ...formData, description: e.target.value });
                setErrors({ ...errors, description: "" });
              }}
              rows={4}
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] resize-y ${
                errors.description ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Describe the incident in detail"
              required
            />
            {errors.description && (
              <span className="text-red-500 text-xs">{errors.description}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Immediate Action Taken
            </label>
            <textarea
              disabled={isView}
              value={formData.immediateActionTaken || ""}
              onChange={(e) => {
                setFormData({ ...formData, immediateActionTaken: e.target.value });
              }}
              rows={3}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] resize-y"
              placeholder="Describe immediate actions taken"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Escalated To <span className="text-red-500">*</span>
            </label>
            {isView ? (
              <p className="text-sm text-gray-800 bg-gray-50 border border-gray-100 rounded-md p-3">
                {escalatedSummary()}
              </p>
            ) : (
              <select
                multiple
                value={formData.escalatedTo || []}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                  setFormData({ ...formData, escalatedTo: selected });
                  setErrors({ ...errors, escalatedTo: "" });
                }}
                className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] ${
                  errors.escalatedTo ? "border-red-500" : "border-gray-300"
                }`}
                size={5}
              >
                {orgUsersData.map((u: any) => (
                  <option key={u._id || u.id} value={u._id || u.id}>
                    {`${u.firstName || ""} ${u.lastName || ""}`.trim() || u.name}
                  </option>
                ))}
              </select>
            )}
            {!isView && (
              <p className="text-xs text-gray-500">Hold Ctrl/Cmd to select multiple recipients.</p>
            )}
            {errors.escalatedTo && (
              <span className="text-red-500 text-xs">{errors.escalatedTo}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Linked evidence — log / system references (optional)
            </label>
            <textarea
              disabled={isView}
              value={linkedLogsText}
              onChange={(e) => setLinkedLogsText(e.target.value)}
              rows={3}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#375998] resize-y font-mono text-sm"
              placeholder={"Access log id, door event id, SIEM correlation id…\n(one per line)"}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Evidence upload (photos / video / PDF)
              {(formData.severity === "High" || formData.severity === "Critical") && (
                <span className="text-red-500"> *</span>
              )}
            </label>
            <FileUpload
              mode={isView ? "view" : "create"}
              files={files}
              onFilesChange={setFiles}
              existingAttachments={existingAttachments}
              onRemoveExistingAttachment={(index) => {
                setExistingAttachments(existingAttachments.filter((_, i) => i !== index));
              }}
              onPreviewFile={(attachment) => {
                window.open(attachment.url, "_blank");
              }}
              uploading={s3Uploading}
              uploadProgress={s3UploadProgress}
              disabled={submitting || isView}
              getRootProps={getRootProps}
              getInputProps={getInputProps}
            />
            {errors.evidence && (
              <span className="text-red-500 text-xs">{errors.evidence}</span>
            )}
            <p className="text-xs text-gray-500">
              Required for High/Critical. Stored as URLs on the security record and copied to the
              organization incident attachments when sync succeeds.
            </p>
          </div>
        </div>
      </form>
    </>
  );
};

export default SecurityIncidentForm;
